<template>
  <Toast />
  <ConfirmDialog />
  <!-- The action plan's bulk accept/un-accept questions (MYAC-1215). Their own
       instance, so a short question gets a compact box instead of the band the
       ungrouped dialog above stretches to — and so changing how a sweep asks
       never changes how anything else in the product asks. Mounted here rather
       than per surface: `ActionPlanGroup` is rendered from three of them. -->
  <ConfirmDialog
    :group="BULK_REVIEW_CONFIRM_GROUP"
    :style="{ width: '22rem' }"
    :breakpoints="{ '640px': '90vw' }"
  />
  <!-- The Continuity page's repeat-search question (MYAC-1612): compact for the
       same reason, its own instance for the same reason. -->
  <ConfirmDialog
    :group="CONTINUITY_SEARCH_CONFIRM_GROUP"
    :style="{ width: '26rem' }"
    :breakpoints="{ '640px': '90vw' }"
  />
  <ReapproveModeDialog />
  <EnvironmentWarningModal v-model:visible="showEnvironmentWarning" @redirect="onRedirectToProduction"
    @continue="onContinueInCurrentEnv" />
  <ContactSalesDialog v-model:visible="contactSalesVisible" :scope="contactSalesScope" />
  <RouterView />
  <!-- Dev-only agents-endpoint switch. It lives in the app SHELL, not in a page,
       so every surface that talks to the agents server (change control, the
       docked MAC panel, any future one) is retargetable without each view
       remembering to mount it. It self-gates on `import.meta.env.DEV` — true only
       under `vite dev`, so this is dead code in every deployed build. -->
  <AgentEndpointSwitch class="!fixed bottom-3 left-3 z-40 shadow-sm" />
</template>
<script setup lang="ts">
import { RouterView, useRoute, useRouter } from 'vue-router'
import { watch, onMounted, onUnmounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import ReapproveModeDialog from '@/components/shared/tasks/ReapproveModeDialog.vue'
import { BULK_REVIEW_CONFIRM_GROUP } from '@/components/referentials/bulkReviewConfirm'
import { CONTINUITY_SEARCH_CONFIRM_GROUP } from '@/components/referentials/continuitySearchConfirm'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app'
import { useVersionStore } from '@/stores/version'
import AgentEndpointSwitch from '@/components/dev/AgentEndpointSwitch.vue'
import EnvironmentWarningModal from '@/components/shared/EnvironmentWarningModal.vue'
import ContactSalesDialog from '@/components/settings/billing/ContactSalesDialog.vue'
import { useSelfServeGate } from '@/composables/useSelfServeGate'

const { contactSalesVisible, contactSalesScope } = useSelfServeGate()

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
appStore.loadPublicConfig()
const { pendingToasts } = storeToRefs(appStore)
const toast = useToast()
const { t } = useI18n()
const versionStore = useVersionStore()
const { isUpdateAvailable } = storeToRefs(versionStore)

// Environment warning modal
const STORAGE_KEY = 'env_warning_choice'
const PRODUCTION_URL = 'https://app.myauditcorner.com'

const isNonProdEnvironment = computed(() => {
  const env = import.meta.env.VITE_ENVIRONMENT
  return env === 'dev' || env === 'staging'
})

/**
 * The warning exists for someone who LANDED on app-dev thinking it was the real
 * app. Nobody lands on their own `vite dev` server by accident — they typed the
 * port — so on the dev server the modal is pure friction: it is `:closable="false"`
 * and returns after every full reload, i.e. after every HMR restart.
 *
 * `import.meta.env.DEV` is true only under `vite dev`, the same signal
 * `AgentEndpointSwitch` gates on, so this is dead code in every deployed build.
 * `VITE_ENV_WARNING=off` is the escape hatch for the cases that are not the dev
 * server (a `preview-dev` build, a demo box) — unset behaves exactly as before.
 */
const skipEnvironmentWarning = computed(
  () => import.meta.env.DEV || import.meta.env.VITE_ENV_WARNING === 'off',
)

const showEnvironmentWarning = ref(false)

function checkEnvironmentWarning() {
  // Only show if non-prod environment
  if (!isNonProdEnvironment.value) {
    return
  }

  if (skipEnvironmentWarning.value) {
    return
  }

  // Check if user has remembered their choice
  const rememberedChoice = localStorage.getItem(STORAGE_KEY)
  if (rememberedChoice === 'redirect') {
    // Auto-redirect if they chose redirect
    window.location.href = PRODUCTION_URL
    return
  }
  if (rememberedChoice === 'continue') {
    // Don't show modal if they chose continue
    return
  }

  // Show modal if no remembered choice
  showEnvironmentWarning.value = true
}

function onRedirectToProduction() {
  window.location.href = PRODUCTION_URL
}

function onContinueInCurrentEnv() {
  showEnvironmentWarning.value = false
}

// Enable debug mode if ?debug=true query parameter is present
watch(() => route.query.debug, (debugParam) => {
  const debugValue = debugParam?.toString().toLowerCase()
  appStore.setDebug(debugValue === 'true' || debugValue === '1')
}, { immediate: true })

watch(pendingToasts, (list) => {
  while (list.length > 0) {
    const msg = list.shift()
    if (!msg) break
    toast.add({
      severity: msg.severity,
      summary: msg.summary,
      detail: msg.detail,
      life: msg.life,
    })
  }
}, { deep: true })

// Start version polling when app mounts
onMounted(async () => {
  versionStore.startPolling()
  // Wait for initial navigation to complete before showing environment warning
  // This ensures auth state is fully resolved and any redirects have happened
  await router.isReady()
  checkEnvironmentWarning()
})

// Stop version polling when app unmounts
onUnmounted(() => {
  versionStore.stopPolling()
})

// Watch for version updates and show toast notification
let updateToastShown = false
watch(isUpdateAvailable, (available) => {
  if (available && !updateToastShown) {
    updateToastShown = true
    // Show persistent toast with reload instruction
    // The message includes instructions to reload manually
    toast.add({
      severity: 'info',
      summary: t('shared.update.available.title'),
      detail: t('shared.update.available.message'),
      life: 0, // Don't auto-dismiss - user must manually reload
      closable: true,
    })
  }
})
</script>
<style lang="scss">
@import 'primeicons/primeicons.css';

// DON'T REMOTE THIS, IT FIX DARK MODE ISSUES
html,
body {
  min-height: max(100vh, 600px);
}

body {
  background-color: #f8fafc;
  font-family: var(--font-family);
  font-weight: 400;
  color: var(--text-color);
}

#app {
  min-height: max(100vh, 600px);
  // Redesign: no 5rem bottom padding. It made every page whose content ends
  // within 80px of the window's bottom scroll — to reveal nothing — so a
  // scrollbar showed on pages with nothing to scroll (Sébastien, 2026-10-01).
  // Pages keep their own bottom spacing (p-4 and up).
  // A column, so a page can fill the window height with flex (the Pilotage
  // chat) rather than calc(100vh - <header>px).
  display: flex;
  flex-direction: column;
}
</style>
